import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import useWindowSize from '../../hooks/useWindowSize';
import { filterInput } from '@/utils/helper';
import './input.css';

export default function Input(props) {
  const {
    value = '',
    isSuccessful = false,
    isUnsuccessful = false,
    type = 'text',
    sizeShift = 768,
    label = '',
    name = '',
    placeholder = '',
    filter,
    onFocus,
    onBlur,
    onChange,
  } = props;
  const { t } = useTranslation();
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const { width } = useWindowSize();

  const IS_OUTLINED = true;
  const IS_ACTIVE = isFocused || value;
  const IS_SUCCESS = isSuccessful && !isUnsuccessful;
  const IS_ERROR = !isSuccessful && isUnsuccessful;

  const isDesktopSize = (width) => {
    return width >= sizeShift;
  };

  const onFocusHandler = (e) => {
    onFocus && onFocus(e);
    setIsFocused(!isFocused);
  };

  const onBlurHandler = (e) => {
    onBlur && onBlur(e);
    setIsFocused(!isFocused);
  };

  const onChangeHandler = (e) => {
    filterInput(e, filter);
    onChange(e);
  };

  const onClickHandler = (e) => {
    e.stopPropagation();
    if (inputRef.current) inputRef.current.focus();
  };

  const rootClassNames = (main, width) => {
    return clsx(main, {
      [`${main}--mobile`]: !isDesktopSize(width || 0),
      [`${main}--desktop`]: isDesktopSize(width || 0),
    });
  };

  const labelClassNames = (main, width) => {
    return clsx(main, {
      [`${main}--outlined`]: IS_OUTLINED,
      [`${main}--focus`]: IS_ACTIVE,
      [`${main}--success`]: IS_SUCCESS,
      [`${main}--error`]: IS_ERROR,
      [`${main}--mobile`]: !isDesktopSize(width || 0),
      [`${main}--desktop`]: isDesktopSize(width || 0),
    });
  };

  const controlClassNames = (main, width) => {
    return clsx(main, {
      [`${main}--outlined`]: IS_OUTLINED,
      [`${main}--focus`]: IS_ACTIVE,
      [`${main}--success`]: IS_SUCCESS,
      [`${main}--error`]: IS_ERROR,
      [`${main}--mobile`]: !isDesktopSize(width || 0),
      [`${main}--desktop`]: isDesktopSize(width || 0),
    });
  };

  // TODO: Placeholder state not clear how to use
  const fieldClassNames = (main, width) => {
    return clsx(main, {
      [`${main}--outlined`]: IS_OUTLINED,
      [`${main}--icon`]: IS_ERROR || IS_SUCCESS,
      [`${main}--focus`]: IS_ACTIVE,
      [`${main}--placeholder`]: getPlaceholder(width || 0),
      [`${main}--mobile`]: !isDesktopSize(width || 0),
      [`${main}--desktop`]: isDesktopSize(width || 0),
    });
  };

  const successClassNames = (main, width) => {
    return clsx(main, {
      [`${main}--outlined`]: IS_OUTLINED,
      [`${main}--visible`]: IS_SUCCESS,
      [`${main}--mobile`]: !isDesktopSize(width || 0),
      [`${main}--desktop`]: isDesktopSize(width || 0),
    });
  };

  const errorClassNames = (main) => {
    return clsx(main, {
      [`${main}--visible`]: IS_ERROR,
    });
  };

  const fieldsetClassNames = (main, width) => {
    return clsx(main, {
      [`${main}--outlined`]: IS_OUTLINED,
      [`${main}--focus`]: IS_ACTIVE,
      [`${main}--success`]: IS_SUCCESS,
      [`${main}--error`]: IS_ERROR,
      [`${main}--no-content`]: value === '',
      [`${main}--mobile`]: !isDesktopSize(width || 0),
      [`${main}--desktop`]: isDesktopSize(width || 0),
    });
  };

  const getPlaceholder = (width) => {
    if (isDesktopSize(width || 0)) {
      return placeholder || label;
    } else {
      return placeholder || '';
    }
  };

  return (
    <div className={rootClassNames('input', width)} onClickCapture={onClickHandler}>
      <label className={labelClassNames('input__label', width)} htmlFor={name}>
        {t(label)}
      </label>
      <div className={controlClassNames('input__control', width)}>
        <input
          ref={inputRef}
          id={name}
          name={name}
          type={type}
          className={fieldClassNames('input__field', width)}
          value={value}
          onFocus={onFocusHandler}
          onBlur={onBlurHandler}
          onChange={onChangeHandler}
          placeholder={t(getPlaceholder(width))}
        />
        <span className={successClassNames('input__success', width)} />
        <span className={errorClassNames('input__error')} />
        <fieldset className={fieldsetClassNames('input__fieldset', width)}>
          <legend className='input__legend'>{t(label)}</legend>
        </fieldset>
      </div>
    </div>
  );
}
